import React, { useEffect, useState } from 'react';
import { TimelineItemType } from 'types/TimelineTypes';
import { TaskType } from '../../views/tasks/TaskTypes';
import { UserType } from '../../views/users/UserTypes';
import { fetchData } from 'services/api/fetchData';
import { useSocketChannel } from 'services/api/updatesSocket';
import TimelineNoteForm from './TimelineNoteForm';
import { GenerateTimelineComponents } from './GenerateTimelineComponents';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useTranslation } from 'react-i18next';
import { useModal } from 'components/modals/ModalContext';
import SecondaryButton from 'components/buttons/SecondaryButton';
import TaskForm from '../../views/tasks/TaskForm';
import EmailForm from '../../views/email/EmailForm';
import { useSettings } from 'services/settings/SettingsContext';
import { useFetchContext } from 'services/api/FetchContext';

interface ApiResponse<TimelineItemType> {
    results: TimelineItemType[];
}

interface TimelineProps {
    placement: 'summary' | 'page';
    timeline_page: string;
    timeline_page_id: number;
    showOnlyTimelineUpdates?: boolean;
    noteOnly?: boolean; // Only offer the note form, for pages without task and email support
    data?: any;
}

const Timeline: React.FC<TimelineProps> = ({
    placement, timeline_page, timeline_page_id, showOnlyTimelineUpdates, noteOnly = false, data
}) => {
    const { t } = useTranslation();
    const { handleLogout, activeFeatures } = useAuthContext();
    const { environmentSettings } = useSettings();
    const { getCache, setCache } = useFetchContext();
    const { initializeModal } = useModal();
    const [loading, setLoading] = useState(true);
    const [timelineItems, setTimelineItems] = useState<TimelineItemType[]>([]);
    const [users, setUsers] = useState<UserType[]>([]);
    const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);
    const [pinnedNote, setPinnedNote] = useState<TimelineItemType | null>(null);
    const [showForm, setShowForm] = useState<'note' | 'task' | 'email' | null>(null);

    // Fetch the data of the timeline
    const fetchTimelineData = async () => {
        try {
            // Start loading
            setLoading(true);

            // Fetch the timeline items, users and task types in parallel
            const [timelineItemsResponse, userResponse, taskTypeResponse]: [
                ApiResponse<TimelineItemType>, ApiResponse<UserType>, ApiResponse<TaskType>
            ] = await Promise.all([
                fetchData({ apiUrl: `get_timeline_item_list/${timeline_page}/${timeline_page_id}`, handleLogout }),
                fetchData({ apiObject: 'user', handleLogout, useCache: true, getCache, setCache }),
                fetchData({ apiObject: 'tasktype', handleLogout, useCache: true, getCache, setCache })
            ]);
            const data: TimelineItemType[] = timelineItemsResponse.results;
            const timelineItems: TimelineItemType[] = data.map(item => ({
                ...item,
                timeline_object_type: (
                item.update ? 'update'
                : item.note ? 'note'
                : item.task ? 'task'
                : item.payment ? 'payment'
                : item.integration_notification ? 'integrationnotification'
                : item.email_reply ? 'email_reply'
                : item.sms_message ? 'sms_message'
                : 'email'
                ) as 'update' | 'note' | 'task' | 'payment' | 'email' | 'email_reply' | 'integrationnotification' | 'sms_message',
            }));

            // Find the pinned note in the fetched data
            const pinnedNote = timelineItems.find(item => item.note?.pinned === true);
            if (pinnedNote) {
                setPinnedNote(pinnedNote);
            }

            // Sort the timeline items: unchecked tasks on top, ordered by date
            const sortedItems = sortTimelineItems(timelineItems);

            setUsers(userResponse.results);
            setTaskTypes(taskTypeResponse.results);
            setTimelineItems(sortedItems);

            // Hide loader when loading is finished
            setLoading(false);
        } catch (error) {
            console.error('Error fetching timeline data:', error);
            setLoading(false);
        }
    };

    // Fetch the data
    useEffect(() => {
        fetchTimelineData();
    }, [timeline_page_id]);

    // Handle refresh timeline
    const handleRefreshTimeline = () => {
        fetchTimelineData();
        setShowForm(null);
    };

    // Sort the timeline items. Show unchecked tasks on top, show the other items below.
    const sortTimelineItems = (items: TimelineItemType[]): TimelineItemType[] => {
        return items.sort((a, b) => {
            // Determine if an item is a still scheduled sms message
            const isScheduledSms = (item: TimelineItemType) => (
                item.timeline_object_type === 'sms_message'
                && ['scheduled', 'retrying'].includes(item.sms_message?.status || '')
            );

            // Assign score based on checked state, scheduled sms scores like open tasks
            const scoreA = (a.timeline_object_type === 'task' && !a.task?.checked) || isScheduledSms(a) ? 2 : 1;
            const scoreB = (b.timeline_object_type === 'task' && !b.task?.checked) || isScheduledSms(b) ? 2 : 1;
    
            // Sort based on score
            if (scoreA !== scoreB) return scoreA - scoreB;
    
            // If scores are equal, sort by ordering_date
            return new Date(a.ordering_date).getTime() - new Date(b.ordering_date).getTime();
        });
    };

    // Handle update timeline items
    const handleUpdateTimelineItems = (newItem: TimelineItemType) => {
        // Unpack the new item
        const { id, deleted, timeline_object_id, timeline_object_type, note } = newItem;

        // Delete the item from the state
        if (deleted) {
            setTimelineItems(prev => prev.filter(item => item.id !== id));

            // When the deleted item is a pinned note, set the pinned note state to null
            if (pinnedNote && pinnedNote.id === id) {
                setPinnedNote(null);
            }

            return;
        }

        // Add or updated the existing item
        setTimelineItems(prev => {
            // If the item is updated, filter it out of the items
            const updated = prev.filter(item => item.id !== id);

            // Sort the timeline items with the updated or the new item
            return sortTimelineItems([...updated, newItem]);
        });

        // Set the pinned note
        if (timeline_object_type === 'note') {
            if (note?.pinned) {
                setPinnedNote(newItem);
            } else if (pinnedNote && timeline_object_id === pinnedNote.timeline_object_id) {
                setPinnedNote(null);
            }
        }
    };

    // Subscribe to the timeline channel to receive new timeline update or payment items
    const { message: timelineMessage } = useSocketChannel('timeline', ['updated_timeline_item']);
    useEffect(() => {
        // Update the updated timeline item
        if (timelineMessage?.type === 'updated_timeline_item') {

            // Get the message content
            const msg = timelineMessage.message;

            // Only show timeline messages for this specific timeline page id
            if (msg.timeline_page_id === timeline_page_id) {

                // Set timeline update item
                if (msg.update) {
                    // Set the timeline item from the received message
                    setTimelineItems((prevState) => {
                        // Create a new timeline item object
                        const newTimelineItem: TimelineItemType = ({ ...msg, timeline_object_type: 'update' })

                        // Delete all items with the same id from the current state
                        const filteredState = prevState.filter(item => item.id !== newTimelineItem.id);

                        // Add the new item to the filtered state
                        return sortTimelineItems([...filteredState, newTimelineItem]);
                    });

                // Set timeline payment item
                } else if (msg.payment) {
                    setTimelineItems((prevState) => {
                        // Create a new timeline item object
                        const newTimelineItem: TimelineItemType = ({ ...msg, timeline_object_type: 'payment' })

                        // Delete all items with the same id from the current state
                        const filteredState = prevState.filter(item => item.id !== newTimelineItem.id);

                        // Add the new item to the filtered state
                        return sortTimelineItems([...filteredState, newTimelineItem]);
                    });
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timelineMessage]);

    // Map the timeline pages to the connected features
    const connectedFeatureMap: Record<string, string> = {
        job: 'jobs',
        proposal: 'proposals',
        order: 'orders',
        invoice: 'invoices',
        invoice_template: 'invoices',
        contact: 'contacts',
        organization: 'contacts',
        person: 'contacts',
        location: 'contacts',
    };

    // Opens or closes the note form
    const handleNoteForm = () => {
        setShowForm('note');

        if (placement === 'summary') {
            initializeModal(
                <TimelineNoteForm
                    placement='modal'
                    timeline_page={timeline_page}
                    timeline_page_id={timeline_page_id}
                    onSubmitSuccess={handleUpdateTimelineItems}
                />, 
                { 
                    modalSize: 'small-medium',
                    title: t('timeline.note.general.add_note_header'),
                    onClose: () => setShowForm(null)
                }
            );
        }
    };

    // Opens or closes the task form
    const handleTaskForm = () => {
        setShowForm('task');

        if (placement === 'summary') {
            initializeModal(
                <TaskForm
                    placement='modal'
                    timeline_page={timeline_page}
                    timeline_page_id={timeline_page_id}
                    onSubmitSuccess={handleUpdateTimelineItems}
                />, 
                { 
                    modalSize: 'small-medium',
                    title: t('button.add_object_label', { object_name: t('task.general.object_name.singular') }),
                    onClose: () => setShowForm(null),
                }
            );
        }
    };

    // Opens or closes the email form
    const handleEmailForm = () => {
        setShowForm('email')

        if (placement === 'summary') {
            initializeModal(
                <EmailForm
                    placement='modal'
                    timeline_page={timeline_page}
                    timeline_page_id={timeline_page_id}
                    divisionId={data?.division ?? null}
                    connectedFeature={connectedFeatureMap[timeline_page] ?? timeline_page}
                    objectData={data}
                    onSubmitSuccess={handleUpdateTimelineItems}
                />, 
                { 
                    modalSize: 'medium',
                    title: t('email.general.compose_email_header'),
                    onClose: () => setShowForm(null),
                }
            );
        }
    };

    return (
        <div className={`timeline ${placement}-timeline`}>
            {loading ? (
                <div className='timeline-loader'>
                    <div className="loader"></div>
                </div>
            ) : (
                <GenerateTimelineComponents 
                    placement={placement}
                    timelineItems={timelineItems}
                    showOnlyTimelineUpdates={showOnlyTimelineUpdates}
                    pinnedNote={pinnedNote}
                    users={users}
                    taskTypes={taskTypes}
                    timeline_page_id={timeline_page_id}
                    timeline_page={timeline_page}
                    onSubmitSuccess={handleUpdateTimelineItems}>
                    {({ renderPinnedNote, timelineComponents }) => (
                        <>
                            {renderPinnedNote()}
                            {!showOnlyTimelineUpdates && (
                                <>
                                    {placement === 'page' && (
                                        <div className='buttonbar'>
                                            <ul>
                                                <li onClick={handleNoteForm}
                                                    className={showForm === 'note' ? 'active' : ''}>
                                                        {t('timeline.note.general.add_note_label')}
                                                </li>
                                                {!noteOnly && activeFeatures.includes('office_tasks') && (
                                                    <li onClick={handleTaskForm}
                                                        className={showForm === 'task' ? 'active' : ''}>
                                                            {t('timeline.task.general.add_task_label')}
                                                    </li>
                                                )}
                                                {!noteOnly && (
                                                    <li onClick={handleEmailForm}
                                                        className={showForm === 'email' ? 'active' : ''}>
                                                            {t('timeline.email.general.add_email_label')}
                                                    </li>
                                                )}
                                            </ul>
                                            {!showForm && (
                                                <div className='blank-note' onClick={handleNoteForm}></div>
                                            )}
                                            {showForm === 'note' && (
                                                <TimelineNoteForm 
                                                    placement='inline'
                                                    timeline_page={timeline_page}
                                                    timeline_page_id={timeline_page_id}
                                                    closeForm={() => setShowForm(null)} 
                                                    onSubmitSuccess={handleUpdateTimelineItems}
                                                />
                                            )}
                                            {showForm === 'task' && (
                                                <div className='timeline-form'>
                                                    <TaskForm
                                                        placement='inline'
                                                        timeline_page={timeline_page}
                                                        timeline_page_id={timeline_page_id}
                                                        closeForm={() => setShowForm(null)} 
                                                        onSubmitSuccess={handleUpdateTimelineItems}
                                                    />
                                                </div>
                                            )}  
                                            {showForm === 'email' && (
                                                <div className='timeline-form'>
                                                    <EmailForm
                                                        placement='inline'
                                                        timeline_page={timeline_page}
                                                        timeline_page_id={timeline_page_id}
                                                        divisionId={data?.division ?? null}
                                                        connectedFeature={connectedFeatureMap[timeline_page] ?? timeline_page}
                                                        objectData={data}
                                                        closeForm={() => setShowForm(null)} 
                                                        onSubmitSuccess={handleUpdateTimelineItems}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {placement === 'summary' && (
                                        <div className='button-bar'>
                                            <SecondaryButton
                                                onClick={handleNoteForm}
                                                isActive={showForm === 'note'}
                                                label='timeline.note.general.add_note_label'
                                                size='extra-small'
                                            />
                                            {!noteOnly && activeFeatures.includes('office_tasks') && (
                                                <SecondaryButton
                                                    onClick={handleTaskForm}
                                                    isActive={showForm === 'task'}
                                                    label='timeline.task.general.add_task_label'
                                                    size='extra-small'
                                                />
                                            )}
                                            {!noteOnly && (
                                                <SecondaryButton
                                                    onClick={handleEmailForm}
                                                    isActive={showForm === 'email'}
                                                    label='timeline.email.general.add_email_label'
                                                    size='extra-small'
                                                />
                                            )}
                                        </div>
                                    )}
                                </>
                            )}            
                            <div>
                                {timelineComponents}
                            </div>
                        </>
                    )}
                </GenerateTimelineComponents>
            )}
        </div>
    );
};

export default Timeline;